import type { PriceRange } from '../../types/generator-config';

interface RangesEditorProps {
    ranges: PriceRange[];
    unit: string;
    onChange: (next: PriceRange[]) => void;
}

/**
 * Éditeur de paliers de prix pour les fields slider/number.
 * Chaque palier définit une plage [min, max) et un montant fixe.
 * Max null = palier ouvert vers le haut.
 */
export function RangesEditor({ ranges, unit, onChange }: RangesEditorProps) {
    const update = (idx: number, patch: Partial<PriceRange>) => {
        onChange(ranges.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };

    const remove = (idx: number) => {
        onChange(ranges.filter((_, i) => i !== idx));
    };

    const add = () => {
        const last = ranges[ranges.length - 1];
        const nextMin = last ? (last.max ?? last.min + 10) : 0;
        onChange([...ranges, { min: nextMin, max: null, amount: 0 }]);
    };

    return (
        <div className="ranges-editor">
            <div className="ranges-header">
                <span className="ranges-col ranges-col-num">De</span>
                <span className="ranges-col ranges-col-num">À</span>
                <span className="ranges-col ranges-col-amount">Montant (€)</span>
                <span className="ranges-col ranges-col-action" aria-hidden />
            </div>

            {ranges.length === 0 ? (
                <div className="rules-empty">
                    Aucun palier. Ajoutez-en un pour commencer.
                </div>
            ) : (
                <ul className="ranges-list">
                    {ranges.map((r, idx) => (
                        <li key={idx} className="ranges-row">
                            <div className="ranges-input-wrap">
                                <input
                                    type="number"
                                    className="ranges-input"
                                    value={r.min}
                                    onChange={(e) =>
                                        update(idx, { min: Number(e.target.value) || 0 })
                                    }
                                />
                                {unit && <span className="ranges-unit">{unit}</span>}
                            </div>
                            <div className="ranges-input-wrap">
                                <input
                                    type="number"
                                    className="ranges-input"
                                    value={r.max ?? ''}
                                    placeholder="∞"
                                    onChange={(e) =>
                                        update(idx, {
                                            max: e.target.value === ''
                                                ? null
                                                : Number(e.target.value) || 0,
                                        })
                                    }
                                />
                                {unit && <span className="ranges-unit">{unit}</span>}
                            </div>
                            <input
                                type="number"
                                className="ranges-input ranges-input-amount"
                                value={r.amount}
                                onChange={(e) =>
                                    update(idx, { amount: Number(e.target.value) || 0 })
                                }
                            />
                            <button
                                type="button"
                                className="icon-btn icon-btn-danger ranges-remove"
                                onClick={() => remove(idx)}
                                aria-label="Supprimer ce palier"
                                title="Supprimer"
                                disabled={ranges.length <= 1}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                </svg>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <button type="button" className="opts-add" onClick={add}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Ajouter un palier
            </button>

            <small className="impact-hint">
                Chaque palier s'applique si <strong>min ≤ valeur &lt; max</strong>.
                Laisser "À" vide = pas de limite haute.
            </small>
        </div>
    );
}
